import React, { useState } from 'react';

import {
  ChakraAvatar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  ChatUser,
  EquipUITheme,
  Popover,
} from '@equip.health/ui';

import { MEMBERS_TO_DISPLAY } from '~/lib/constants/chat.constants';

const idPrefix = 'members';
const idPostfix = 'popover';

const { label, labelBold, caption, h6 } = EquipUITheme.typography;
const popoverBody = (id: string, members: ChatUser[]) => {
  return (
    <ChakraBox id={`${idPrefix}-popover-container__${id}__${idPostfix}`}>
      {(members ?? []).map((member) => (
        <ChakraFlex
          id={`${idPrefix}-popover-wrapper__${member.userId}__${idPostfix}`}
          key={`members-popover-${member?.userId}`}
          padding="10px"
        >
          <ChakraAvatar
            background="orange.50"
            borderColor="neutral.primary"
            borderWidth="4px"
            color="black"
            id={member?.userId}
            name={member?.nickname}
            showBorder
            size="md"
          />
          <ChakraBox
            id={`${idPrefix}-nickname-wrapper__${member.userId}__${idPostfix}`}
            paddingLeft="12px"
          >
            <ChakraText
              color="neutral.primary"
              {...h6}
              id={`${idPrefix}-nickname__${member.userId}__${idPostfix}`}
            >
              {member?.nickname}
            </ChakraText>
            <ChakraText
              color="neutral.secondary"
              id={`${idPrefix}-provider-type__${member.userId}__${idPostfix}`}
              {...caption}
              lineHeight="16.8px"
            >
              {member?.metaData?.providerType}
            </ChakraText>
          </ChakraBox>
        </ChakraFlex>
      ))}
    </ChakraBox>
  );
};

interface MembersPopoverListProps {
  id: string;
  members: ChatUser[];
}

const MembersPopoverList = React.forwardRef<
  HTMLDivElement,
  MembersPopoverListProps
>(
  (
    { id, members }: MembersPopoverListProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [showUserPopover, setShowUserPopover] = useState<boolean>(false);

    return (
      <Popover
        bodyContent={popoverBody(id, members)}
        headerContent={
          <ChakraFlex
            id={`${idPrefix}-group-member-container__${id}__${idPostfix}`}
            padding="10px"
          >
            <ChakraText
              color="neutral.secondary"
              {...label}
              id={`${idPrefix}-group-members-label__${id}__${idPostfix}`}
            >
              Group Members
            </ChakraText>
          </ChakraFlex>
        }
        id={id}
        isOpen={showUserPopover}
        onClose={() => setShowUserPopover(false)}
        onOpen={() => setShowUserPopover(true)}
        ref={ref}
        size="md"
        triggerContent={
          <ChakraText
            color="neutral.secondary"
            left={`-${8 * MEMBERS_TO_DISPLAY}px`}
            position="relative"
            {...labelBold}
            cursor="pointer"
            id={`${idPrefix}-popover-trigger__${id}__${idPostfix}`}
          >
            and {members.length - MEMBERS_TO_DISPLAY} more
          </ChakraText>
        }
      />
    );
  },
);

MembersPopoverList.displayName = 'MembersPopoverList';

export default MembersPopoverList;
